import { useBottomSheetInternal } from '@gorhom/bottom-sheet';
import React, {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {
    findNodeHandle,
    type NativeSyntheticEvent,
    TextInput as RNTextInput,
    type TextInputFocusEventData,
} from 'react-native';
import { type Input as BaseInput } from 'tamagui';

import { isWeb } from '../../constants';
import { StyledH5Input } from './StyledInputs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StateChangeHandler = (args: any) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BottomSheetTextInputProps<T extends React.ElementType<any>> =
    React.ComponentPropsWithRef<T> & {
        onFocus?: StateChangeHandler;
        onBlur?: StateChangeHandler;
        component?: T;
        children?: ReactNode;
    };

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
    if (typeof ref === 'function') {
        ref(value);
    } else if (ref != null && 'current' in ref) {
        (ref as React.MutableRefObject<T | null>).current = value;
    }
}

// Ensures a text input coordinates with @gorhom/bottom-sheet v5 keyboard handling.
// Aligned with BottomSheetTextInput (animatedKeyboardState / textInputNodesRef).
const BaseKeyboardCompatibleBottomSheetTextInput = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends React.ElementType<any>,
>(
    {
        onFocus,
        onBlur,
        component: Component = StyledH5Input as unknown as T, // Default to StyledH5Input if no component is provided
        ...rest
    }: BottomSheetTextInputProps<T>,
    ref: React.Ref<BaseInput>,
): ReactElement => {
    const { animatedKeyboardState, textInputNodesRef } =
        useBottomSheetInternal();
    const innerRef = useRef<BaseInput | null>(null);

    const setRefs = useCallback(
        (instance: BaseInput | null) => {
            innerRef.current = instance;
            assignRef(ref, instance);
        },
        [ref],
    );

    useEffect(() => {
        const componentNode = findNodeHandle(
            innerRef.current as unknown as Parameters<typeof findNodeHandle>[0],
        );
        if (componentNode == null) {
            return;
        }

        if (!textInputNodesRef.current.has(componentNode)) {
            textInputNodesRef.current.add(componentNode);
        }

        return () => {
            const node = findNodeHandle(
                innerRef.current as unknown as Parameters<
                    typeof findNodeHandle
                >[0],
            );
            if (node == null) {
                return;
            }

            const keyboardState = animatedKeyboardState.get();
            if (keyboardState.target === node) {
                animatedKeyboardState.set((state) => ({
                    ...state,
                    target: undefined,
                }));
            }

            if (textInputNodesRef.current.has(node)) {
                textInputNodesRef.current.delete(node);
            }
        };
    }, [textInputNodesRef, animatedKeyboardState]);

    const handleOnFocus = useCallback(
        (args: NativeSyntheticEvent<TextInputFocusEventData>) => {
            animatedKeyboardState.set((state) => ({
                ...state,
                target: args.nativeEvent.target,
            }));
            if (onFocus !== undefined && typeof onFocus === 'function') {
                (onFocus as StateChangeHandler)(args);
            }
        },
        [onFocus, animatedKeyboardState],
    );

    const handleOnBlur = useCallback(
        (args: NativeSyntheticEvent<TextInputFocusEventData>) => {
            const keyboardState = animatedKeyboardState.get();
            const focused = RNTextInput.State.currentlyFocusedInput();
            const currentFocusedInput = findNodeHandle(
                focused as unknown as Parameters<typeof findNodeHandle>[0],
            );

            const shouldRemoveCurrentTarget =
                keyboardState.target === args.nativeEvent.target;
            const shouldIgnoreBlurEvent =
                currentFocusedInput != null &&
                textInputNodesRef.current.has(currentFocusedInput);

            if (shouldRemoveCurrentTarget && !shouldIgnoreBlurEvent) {
                animatedKeyboardState.set((state) => ({
                    ...state,
                    target: undefined,
                }));
            }

            if (onBlur !== undefined && typeof onBlur === 'function') {
                (onBlur as StateChangeHandler)(args);
            }
        },
        [onBlur, animatedKeyboardState, textInputNodesRef],
    );

    return (
        <Component
            ref={setRefs}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            {...rest}
        />
    );
};

const BaseBasicTextInput = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends React.ElementType<any>,
>(
    {
        onFocus,
        onBlur,
        component: Component = StyledH5Input as unknown as T, // Default to StyledH5Input if no component is provided
        ...rest
    }: BottomSheetTextInputProps<T>,
    ref: React.Ref<BaseInput>,
): ReactElement => {
    return (
        <Component
            ref={ref as React.RefObject<BaseInput>}
            onFocus={onFocus as StateChangeHandler}
            onBlur={onBlur as StateChangeHandler}
            {...rest}
        />
    );
};

export const KeyboardCompatibleBottomSheetTextInput = React.forwardRef(
    isWeb ? BaseBasicTextInput : BaseKeyboardCompatibleBottomSheetTextInput,
);
